import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  Spinner,
  SpinnerSize,
  Dialog,
  DialogType,
  DialogFooter,
  IconButton,
  Separator,
  Dropdown,
  IDropdownOption,
  Label
} from '@fluentui/react';
import {
  EditRegular,
  DeleteRegular,
  AddRegular,
  SaveRegular,
  ArrowResetRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  DocumentRegular
} from '@fluentui/react-icons';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './AdminGuideEditor.module.css';

// Types matching backend structure
interface BaseTile {
  id: string;
  type: string;
}

interface ConnectorTile extends BaseTile {
  type: 'connector';
  icon: string;
  name: string;
  description: string;
  configItems: string[];
  bestFor: string;
}

interface InfoBoxTile extends BaseTile {
  type: 'info-box';
  variant: 'info' | 'concept' | 'tip' | 'troubleshoot';
  title: string;
  content: string;
  listItems?: string[];
}

interface StepGuideTile extends BaseTile {
  type: 'step-guide';
  stepNumber: number;
  title: string;
  content: string;
  listItems?: string[];
}

type Tile = ConnectorTile | InfoBoxTile | StepGuideTile;

interface GuideSection {
  id: string;
  title: string;
  iconName: string;
  intro: string;
  tiles: Tile[];
  lastModified?: string;
}

interface AdminGuideEditorProps {
  isVisible: boolean;
  onDismiss: () => void;
}

const iconOptions: IDropdownOption[] = [
  { key: 'database', text: '🗄️ Database' },
  { key: 'cloud', text: '☁️ Cloud' },
  { key: 'brain', text: '🧠 Brain' },
  { key: 'search', text: '🔍 Search' },
  { key: 'settings', text: '⚙️ Settings' },
  { key: 'lightbulb', text: '💡 Lightbulb' },
  { key: 'document', text: '📄 Document' },
  { key: 'tag', text: '🏷️ Tag' },
  { key: 'question', text: '❓ Question' }
];

const tileTypeOptions: IDropdownOption[] = [
  { key: 'info-box', text: 'Info Box' },
  { key: 'connector', text: 'Connector Card' },
  { key: 'step-guide', text: 'Step Guide' }
];

const infoBoxVariantOptions: IDropdownOption[] = [
  { key: 'concept', text: '📘 Concept (blue)' },
  { key: 'info', text: '💡 Info (yellow)' },
  { key: 'tip', text: '✅ Tip (green)' },
  { key: 'troubleshoot', text: '❓ Troubleshoot (Q&A)' }
];

const AdminGuideEditor: React.FC<AdminGuideEditorProps> = ({ isVisible, onDismiss }) => {
  const [sections, setSections] = useState<GuideSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: MessageBarType; text: string } | null>(null);
  
  // Edit states
  const [editingSection, setEditingSection] = useState<GuideSection | null>(null);
  const [editingTile, setEditingTile] = useState<Tile | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  
  // Dialog states
  const [isSectionPanelOpen, setIsSectionPanelOpen] = useState(false);
  const [isTilePanelOpen, setIsTilePanelOpen] = useState(false);
  const [resetDialogVisible, setResetDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'section' | 'tile'; sectionId: string; tileId?: string } | null>(null);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isVisible) {
      loadSections();
    }
  }, [isVisible]);

  const loadSections = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/help/admin-guide');
      const data = await response.json();
      
      if (data.success && data.sections) {
        setSections(data.sections);
      } else {
        setMessage({ type: MessageBarType.error, text: data.error || 'Failed to load admin guide' });
      }
    } catch (error) {
      setMessage({ type: MessageBarType.error, text: 'Error loading admin guide sections' });
    } finally {
      setLoading(false);
    }
  };

  const saveSections = async () => {
    try {
      setSaving(true);
      const response = await authenticatedFetch('/api/help/admin-guide', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sections })
      });
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: MessageBarType.success, text: 'Admin guide saved successfully' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: MessageBarType.error, text: data.error || 'Failed to save' });
      }
    } catch (error) {
      setMessage({ type: MessageBarType.error, text: 'Error saving admin guide' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    try {
      setSaving(true);
      const response = await authenticatedFetch('/api/help/admin-guide/reset', {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        setMessage({ type: MessageBarType.success, text: 'Reset to defaults successfully' });
        await loadSections();
      } else {
        setMessage({ type: MessageBarType.error, text: data.error || 'Failed to reset' });
      }
    } catch (error) {
      setMessage({ type: MessageBarType.error, text: 'Error resetting admin guide' });
    } finally {
      setSaving(false);
      setResetDialogVisible(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Section CRUD
  const handleAddSection = () => {
    const newSection: GuideSection = {
      id: `section_${Date.now()}`,
      title: '',
      iconName: 'settings',
      intro: '',
      tiles: []
    };
    setEditingSection(newSection);
    setIsSectionPanelOpen(true);
  };

  const handleEditSection = (section: GuideSection) => {
    setEditingSection({ ...section });
    setIsSectionPanelOpen(true);
  };

  const handleSaveSection = () => {
    if (!editingSection) return;
    
    const existingIndex = sections.findIndex(s => s.id === editingSection.id);
    if (existingIndex >= 0) {
      // Update existing
      const updated = [...sections];
      updated[existingIndex] = editingSection;
      setSections(updated);
    } else {
      // Add new
      setSections([...sections, editingSection]);
    }
    
    setIsSectionPanelOpen(false);
    setEditingSection(null);
  };

  const handleDeleteSection = (sectionId: string) => {
    setDeleteTarget({ type: 'section', sectionId });
    setDeleteDialogVisible(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    
    if (deleteTarget.type === 'section') {
      setSections(sections.filter(s => s.id !== deleteTarget.sectionId));
    } else if (deleteTarget.type === 'tile' && deleteTarget.tileId) {
      setSections(sections.map(s => {
        if (s.id === deleteTarget.sectionId) {
          return { ...s, tiles: s.tiles.filter(t => t.id !== deleteTarget.tileId) };
        }
        return s;
      }));
    }
    
    setDeleteDialogVisible(false);
    setDeleteTarget(null);
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === sections.length - 1) return;
    
    const newSections = [...sections];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[swapIndex]] = [newSections[swapIndex], newSections[index]];
    setSections(newSections);
  };

  // Tile CRUD
  const handleAddTile = (sectionId: string) => {
    const newTile: InfoBoxTile = {
      id: `tile_${Date.now()}`,
      type: 'info-box',
      variant: 'concept',
      title: '',
      content: '',
      listItems: []
    };
    setEditingTile(newTile);
    setEditingSectionId(sectionId);
    setIsTilePanelOpen(true);
  };

  const handleEditTile = (sectionId: string, tile: Tile) => {
    setEditingTile({ ...tile });
    setEditingSectionId(sectionId);
    setIsTilePanelOpen(true);
  };

  const handleSaveTile = () => {
    if (!editingTile || !editingSectionId) return;
    
    setSections(sections.map(s => {
      if (s.id === editingSectionId) {
        const existingIndex = s.tiles.findIndex(t => t.id === editingTile.id);
        if (existingIndex >= 0) {
          const updatedTiles = [...s.tiles];
          updatedTiles[existingIndex] = editingTile;
          return { ...s, tiles: updatedTiles };
        } else {
          return { ...s, tiles: [...s.tiles, editingTile] };
        }
      }
      return s;
    }));
    
    setIsTilePanelOpen(false);
    setEditingTile(null);
    setEditingSectionId(null);
  };

  const handleDeleteTile = (sectionId: string, tileId: string) => {
    setDeleteTarget({ type: 'tile', sectionId, tileId });
    setDeleteDialogVisible(true);
  };

  const moveTile = (sectionId: string, tileId: string, direction: 'up' | 'down') => {
    setSections(sections.map(s => {
      if (s.id === sectionId) {
        const index = s.tiles.findIndex(t => t.id === tileId);
        if (index === -1) return s;
        if (direction === 'up' && index === 0) return s;
        if (direction === 'down' && index === s.tiles.length - 1) return s;
        
        const newTiles = [...s.tiles];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;
        [newTiles[index], newTiles[swapIndex]] = [newTiles[swapIndex], newTiles[index]];
        return { ...s, tiles: newTiles };
      }
      return s;
    }));
  };

  const renderTileEditor = () => {
    if (!editingTile) return null;

    const updateTile = (updates: Partial<Tile>) => {
      setEditingTile({ ...editingTile, ...updates } as Tile);
    };

    const handleListItemsChange = (value: string) => {
      const items = value.split('\n').filter(item => item.trim());
      updateTile({ listItems: items });
    };

    return (
      <Stack tokens={{ childrenGap: 16 }}>
        <Dropdown
          label="Tile Type"
          selectedKey={editingTile.type}
          options={tileTypeOptions}
          onChange={(_, option) => {
            if (!option) return;
            const newType = option.key as string;
            
            // Reset tile with new type
            if (newType === 'info-box') {
              setEditingTile({
                id: editingTile.id,
                type: 'info-box',
                variant: 'concept',
                title: (editingTile as any).title || '',
                content: (editingTile as any).content || '',
                listItems: (editingTile as any).listItems || []
              });
            } else if (newType === 'connector') {
              setEditingTile({
                id: editingTile.id,
                type: 'connector',
                icon: '🔍',
                name: (editingTile as any).title || (editingTile as any).name || '',
                description: (editingTile as any).content || (editingTile as any).description || '',
                configItems: (editingTile as any).listItems || (editingTile as any).configItems || [],
                bestFor: (editingTile as any).bestFor || ''
              });
            } else if (newType === 'step-guide') {
              setEditingTile({
                id: editingTile.id,
                type: 'step-guide',
                stepNumber: 1,
                title: (editingTile as any).title || '',
                content: (editingTile as any).content || '',
                listItems: (editingTile as any).listItems || []
              });
            }
          }}
        />

        {editingTile.type === 'info-box' && (
          <>
            <Dropdown
              label="Variant"
              selectedKey={(editingTile as InfoBoxTile).variant}
              options={infoBoxVariantOptions}
              onChange={(_, option) => option && updateTile({ variant: option.key as any })}
            />
            <TextField
              label="Title"
              value={(editingTile as InfoBoxTile).title}
              onChange={(_, val) => updateTile({ title: val || '' })}
            />
            <TextField
              label="Content"
              multiline
              rows={3}
              value={(editingTile as InfoBoxTile).content}
              onChange={(_, val) => updateTile({ content: val || '' })}
            />
            <TextField
              label="List Items (one per line)"
              multiline
              rows={5}
              value={((editingTile as InfoBoxTile).listItems || []).join('\n')}
              onChange={(_, val) => handleListItemsChange(val || '')}
            />
          </>
        )}

        {editingTile.type === 'connector' && (
          <>
            <TextField
              label="Icon (emoji)"
              value={(editingTile as ConnectorTile).icon}
              onChange={(_, val) => updateTile({ icon: val || '🔍' })}
              styles={{ root: { maxWidth: 100 } }}
            />
            <TextField
              label="Name"
              value={(editingTile as ConnectorTile).name}
              onChange={(_, val) => updateTile({ name: val || '' })}
            />
            <TextField
              label="Description"
              multiline
              rows={2}
              value={(editingTile as ConnectorTile).description}
              onChange={(_, val) => updateTile({ description: val || '' })}
            />
            <TextField
              label="Configuration Items (one per line)"
              multiline
              rows={5}
              value={((editingTile as ConnectorTile).configItems || []).join('\n')}
              onChange={(_, val) => {
                const items = (val || '').split('\n').filter(item => item.trim());
                updateTile({ configItems: items });
              }}
            />
            <TextField
              label="Best For"
              value={(editingTile as ConnectorTile).bestFor}
              onChange={(_, val) => updateTile({ bestFor: val || '' })}
            />
          </>
        )}

        {editingTile.type === 'step-guide' && (
          <>
            <TextField
              label="Step Number"
              type="number"
              value={String((editingTile as StepGuideTile).stepNumber)}
              onChange={(_, val) => updateTile({ stepNumber: parseInt(val || '1') || 1 })}
              styles={{ root: { maxWidth: 100 } }}
            />
            <TextField
              label="Title"
              value={(editingTile as StepGuideTile).title}
              onChange={(_, val) => updateTile({ title: val || '' })}
            />
            <TextField
              label="Content"
              multiline
              rows={3}
              value={(editingTile as StepGuideTile).content}
              onChange={(_, val) => updateTile({ content: val || '' })}
            />
            <TextField
              label="List Items (one per line)"
              multiline
              rows={5}
              value={((editingTile as StepGuideTile).listItems || []).join('\n')}
              onChange={(_, val) => handleListItemsChange(val || '')}
            />
          </>
        )}
      </Stack>
    );
  };

  if (loading) {
    return (
      <Panel
        isOpen={isVisible}
        onDismiss={onDismiss}
        type={PanelType.large}
        headerText="Admin Guide Editor"
        closeButtonAriaLabel="Close"
      >
        <Stack horizontalAlign="center" verticalAlign="center" style={{ height: 300 }}>
          <Spinner size={SpinnerSize.large} />
          <Text>Loading admin guide...</Text>
        </Stack>
      </Panel>
    );
  }

  return (
    <>
      <Panel
        isOpen={isVisible}
        onDismiss={onDismiss}
        type={PanelType.large}
        headerText="Admin Guide Editor"
        closeButtonAriaLabel="Close"
        className={styles.editorPanel}
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton
              text={saving ? 'Saving...' : 'Save All Changes'}
              onClick={saveSections}
              disabled={saving}
              iconProps={{ iconName: 'Save' }}
            />
            <DefaultButton
              text="Reset to Defaults"
              onClick={() => setResetDialogVisible(true)}
              disabled={saving}
            />
            <DefaultButton text="Close" onClick={onDismiss} />
          </Stack>
        )}
        isFooterAtBottom={true}
      >
        <Stack tokens={{ childrenGap: 16 }}>
          {message && (
            <MessageBar
              messageBarType={message.type}
              onDismiss={() => setMessage(null)}
            >
              {message.text}
            </MessageBar>
          )}

          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text variant="mediumPlus" style={{ fontWeight: 600 }}>
              Sections ({sections.length})
            </Text>
            <PrimaryButton
              text="Add Section"
              iconProps={{ iconName: 'Add' }}
              onClick={handleAddSection}
            />
          </Stack>

          <div className={styles.sectionsList}>
            {sections.map((section, sectionIndex) => (
              <div key={section.id} className={styles.sectionCard}>
                <div 
                  className={styles.sectionHeader}
                  onClick={() => toggleSection(section.id)}
                >
                  <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                    {expandedSections.has(section.id) ? 
                      <ChevronDownRegular className={styles.chevron} /> : 
                      <ChevronUpRegular className={styles.chevron} />
                    }
                    <Text className={styles.sectionTitle}>{section.title || '(Untitled)'}</Text>
                    <Text className={styles.sectionMeta}>{section.tiles.length} tiles</Text>
                  </Stack>
                  
                  <Stack horizontal tokens={{ childrenGap: 4 }} onClick={e => e.stopPropagation()}>
                    <IconButton
                      iconProps={{ iconName: 'Up' }}
                      title="Move Up"
                      disabled={sectionIndex === 0}
                      onClick={() => moveSection(section.id, 'up')}
                    />
                    <IconButton
                      iconProps={{ iconName: 'Down' }}
                      title="Move Down"
                      disabled={sectionIndex === sections.length - 1}
                      onClick={() => moveSection(section.id, 'down')}
                    />
                    <IconButton
                      iconProps={{ iconName: 'Edit' }}
                      title="Edit Section"
                      onClick={() => handleEditSection(section)}
                    />
                    <IconButton
                      iconProps={{ iconName: 'Delete' }}
                      title="Delete Section"
                      onClick={() => handleDeleteSection(section.id)}
                    />
                  </Stack>
                </div>

                {expandedSections.has(section.id) && (
                  <div className={styles.sectionBody}>
                    <div className={styles.sectionIntro}>
                      <Label>Intro:</Label>
                      <Text>{section.intro || '(No intro)'}</Text>
                    </div>
                    
                    <Separator />
                    
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                      <Text style={{ fontWeight: 600 }}>Tiles</Text>
                      <DefaultButton
                        text="Add Tile"
                        iconProps={{ iconName: 'Add' }}
                        onClick={() => handleAddTile(section.id)}
                      />
                    </Stack>

                    <div className={styles.tilesList}>
                      {section.tiles.map((tile, tileIndex) => (
                        <div key={tile.id} className={styles.tileCard}>
                          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <DocumentRegular className={styles.tileIcon} />
                            <Stack>
                              <Text className={styles.tileTitle}>
                                {tile.type === 'connector' ? (tile as ConnectorTile).name :
                                 tile.type === 'info-box' ? (tile as InfoBoxTile).title :
                                 tile.type === 'step-guide' ? `Step ${(tile as StepGuideTile).stepNumber}: ${(tile as StepGuideTile).title}` :
                                 'Unknown'}
                              </Text>
                              <Text className={styles.tileType}>{tile.type}</Text>
                            </Stack>
                          </Stack>
                          
                          <Stack horizontal tokens={{ childrenGap: 4 }}>
                            <IconButton
                              iconProps={{ iconName: 'Up' }}
                              title="Move Up"
                              disabled={tileIndex === 0}
                              onClick={() => moveTile(section.id, tile.id, 'up')}
                            />
                            <IconButton
                              iconProps={{ iconName: 'Down' }}
                              title="Move Down"
                              disabled={tileIndex === section.tiles.length - 1}
                              onClick={() => moveTile(section.id, tile.id, 'down')}
                            />
                            <IconButton
                              iconProps={{ iconName: 'Edit' }}
                              title="Edit Tile"
                              onClick={() => handleEditTile(section.id, tile)}
                            />
                            <IconButton
                              iconProps={{ iconName: 'Delete' }}
                              title="Delete Tile"
                              onClick={() => handleDeleteTile(section.id, tile.id)}
                            />
                          </Stack>
                        </div>
                      ))}
                      
                      {section.tiles.length === 0 && (
                        <Text className={styles.emptyTiles}>No tiles. Click "Add Tile" to create one.</Text>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Stack>
      </Panel>

      {/* Section Edit Panel */}
      <Panel
        isOpen={isSectionPanelOpen}
        onDismiss={() => { setIsSectionPanelOpen(false); setEditingSection(null); }}
        type={PanelType.medium}
        headerText={editingSection && sections.find(s => s.id === editingSection.id) ? 'Edit Section' : 'Add Section'}
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton text="Save" onClick={handleSaveSection} />
            <DefaultButton text="Cancel" onClick={() => { setIsSectionPanelOpen(false); setEditingSection(null); }} />
          </Stack>
        )}
        isFooterAtBottom={true}
      >
        {editingSection && (
          <Stack tokens={{ childrenGap: 16 }}>
            <TextField
              label="Section ID"
              value={editingSection.id}
              disabled
              description="Auto-generated unique identifier"
            />
            <TextField
              label="Title"
              value={editingSection.title}
              onChange={(_, val) => setEditingSection({ ...editingSection, title: val || '' })}
              required
            />
            <Dropdown
              label="Icon"
              selectedKey={editingSection.iconName}
              options={iconOptions}
              onChange={(_, option) => option && setEditingSection({ ...editingSection, iconName: option.key as string })}
            />
            <TextField
              label="Introduction"
              multiline
              rows={4}
              value={editingSection.intro}
              onChange={(_, val) => setEditingSection({ ...editingSection, intro: val || '' })}
              description="Displayed at the top of the section"
            />
          </Stack>
        )}
      </Panel>

      {/* Tile Edit Panel */}
      <Panel
        isOpen={isTilePanelOpen}
        onDismiss={() => { setIsTilePanelOpen(false); setEditingTile(null); setEditingSectionId(null); }}
        type={PanelType.medium}
        headerText={editingTile && sections.some(s => s.tiles.some(t => t.id === editingTile.id)) ? 'Edit Tile' : 'Add Tile'}
        onRenderFooterContent={() => (
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <PrimaryButton text="Save" onClick={handleSaveTile} />
            <DefaultButton text="Cancel" onClick={() => { setIsTilePanelOpen(false); setEditingTile(null); setEditingSectionId(null); }} />
          </Stack>
        )}
        isFooterAtBottom={true}
      >
        {renderTileEditor()}
      </Panel>

      {/* Reset Confirmation Dialog */}
      <Dialog
        hidden={!resetDialogVisible}
        onDismiss={() => setResetDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Reset to Defaults',
          subText: 'This will replace all current content with the default admin guide. This action cannot be undone.'
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={resetToDefaults} text="Reset" />
          <DefaultButton onClick={() => setResetDialogVisible(false)} text="Cancel" />
        </DialogFooter>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        hidden={!deleteDialogVisible}
        onDismiss={() => setDeleteDialogVisible(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: `Delete ${deleteTarget?.type === 'section' ? 'Section' : 'Tile'}`,
          subText: `Are you sure you want to delete this ${deleteTarget?.type}? This action cannot be undone.`
        }}
      >
        <DialogFooter>
          <PrimaryButton onClick={confirmDelete} text="Delete" />
          <DefaultButton onClick={() => setDeleteDialogVisible(false)} text="Cancel" />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default AdminGuideEditor;
